/**
 * ConversationHistory — the landlord's past help conversations: open one, start a new one, or delete one
 * after an inline confirmation (BR-36). Conversations are purged 90 days after their last message.
 */

import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { ConversationListItem } from '@resida/api-types/help';
import { Button } from '../ui/button';
import { useDeleteHelpConversation, useHelpConversations } from '../../lib/api.help';
import { HelpLoadError, RowsSkeleton } from './ArticleList';
import { conversationDate } from './conversation-date';

interface DeleteConfirmProps {
  conversation: ConversationListItem;
  isDeleting: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteConfirm({ conversation, isDeleting, failed, onCancel, onConfirm }: DeleteConfirmProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  // The safe action gets focus so an accidental Enter cancels.
  useEffect(() => cancelRef.current?.focus(), []);

  return (
    <div className="flex flex-col gap-2 border-b border-border px-1 py-3" data-testid="help-delete-confirm">
      <div>
        <p className="text-sm font-semibold text-foreground">{conversation.title ?? 'New conversation'}</p>
        <p className="text-[13px] text-muted-foreground">{conversationDate(conversation.updatedAt)}</p>
      </div>
      <div
        role="alertdialog"
        aria-label="Delete this conversation?"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-[13px] text-foreground"
      >
        Delete this conversation and its ratings? This cannot be undone.
      </div>
      {failed && (
        <p role="alert" className="text-xs text-destructive" data-testid="help-delete-error">
          We couldn't delete the conversation. Try again.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          ref={cancelRef}
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isDeleting}
          data-testid="help-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          className="flex-1"
          onClick={onConfirm}
          disabled={isDeleting}
          data-testid="help-delete-confirm-button"
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

interface ConversationHistoryProps {
  onOpen: (conversationId: string) => void;
  onNew: () => void;
  /** Called after a conversation is deleted, so the caller can clear it if it was the open one. */
  onDeleted: (conversationId: string) => void;
}

export function ConversationHistory({ onOpen, onNew, onDeleted }: ConversationHistoryProps) {
  const { data, isLoading, isError, refetch } = useHelpConversations(true);
  const remove = useDeleteHelpConversation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // Where focus goes once the inline confirm has closed: a row's trash button, or the New conversation button.
  const [focusTarget, setFocusTarget] = useState<string | 'new' | null>(null);
  const trashButtons = useRef(new Map<string, HTMLButtonElement>());
  const newButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focusTarget === null || confirmingId !== null) return;
    (focusTarget === 'new' ? newButton.current : trashButtons.current.get(focusTarget))?.focus();
    setFocusTarget(null);
  }, [focusTarget, confirmingId]);

  const cancelDelete = (id: string) => {
    remove.reset();
    setConfirmingId(null);
    setFocusTarget(id);
  };

  const confirmDelete = (id: string) => {
    // After a delete focus moves to the next row's trash button, else the previous one, else "New conversation".
    const rows = data ?? [];
    const index = rows.findIndex((row) => row.id === id);
    const neighbour = rows[index + 1] ?? rows[index - 1];
    remove.mutate(id, {
      onSuccess: () => {
        setConfirmingId(null);
        setFocusTarget(neighbour?.id ?? 'new');
        onDeleted(id);
      },
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4" data-testid="help-history">
      <Button ref={newButton} type="button" onClick={onNew} data-testid="help-new-conversation">
        <Plus aria-hidden="true" />
        New conversation
      </Button>

      {isLoading && <RowsSkeleton data-testid="help-history-loading" />}
      {isError && (
        <HelpLoadError
          message="We couldn't load your conversations."
          onRetry={() => void refetch()}
          data-testid="help-history-error"
        />
      )}
      {data && data.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="help-history-empty">
          No conversations yet.
        </p>
      )}
      {data && data.length > 0 && (
        <ul className="flex flex-col" aria-label="Conversations">
          {data.map((conversation) =>
            confirmingId === conversation.id ? (
              <li key={conversation.id}>
                <DeleteConfirm
                  conversation={conversation}
                  isDeleting={remove.isPending}
                  failed={remove.isError}
                  onCancel={() => cancelDelete(conversation.id)}
                  onConfirm={() => confirmDelete(conversation.id)}
                />
              </li>
            ) : (
              <li key={conversation.id} className="flex items-center gap-1 border-b border-border last:border-b-0">
                <button
                  type="button"
                  onClick={() => onOpen(conversation.id)}
                  data-testid={`help-history-item-${conversation.id}`}
                  className="flex min-h-[56px] min-w-0 flex-1 flex-col items-start justify-center rounded-md px-1 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="w-full truncate text-sm font-semibold text-foreground">
                    {conversation.title ?? 'New conversation'}
                  </span>
                  <span className="text-[13px] text-muted-foreground">{conversationDate(conversation.updatedAt)}</span>
                </button>
                <button
                  ref={(element) => {
                    if (element) trashButtons.current.set(conversation.id, element);
                    else trashButtons.current.delete(conversation.id);
                  }}
                  type="button"
                  aria-label={`Delete conversation: ${conversation.title ?? 'New conversation'}`}
                  onClick={() => {
                    remove.reset();
                    setConfirmingId(conversation.id);
                  }}
                  data-testid={`help-history-delete-${conversation.id}`}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      <p className="text-xs text-muted-foreground" data-testid="help-history-retention">
        Conversations are deleted automatically 90 days after their last message.
      </p>
    </div>
  );
}
